import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { BackofficeCartridgeService } from '@markant-platform/backoffice/data-access';
import { catchError, EMPTY, firstValueFrom, Observable, tap } from 'rxjs';
import { CMSLanguageService } from '@markant-platform/shared/data-access';
import { OrganizationService } from '@markant-platform/shared/organization/data-access';
import { UserService } from '@markant-platform/shared/user/data-access';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { RegexPattern } from '@markant-platform/shared/util-helpers';
import { SnackbarService } from '@markant-platform/shared/snackbar/feature';
import { StaticRole, UserDTO } from '@markant-platform/shared/user/model';
import {
  BookingCartridgeData,
  BookingCartridgeVariantData,
  BookingEzrDraft,
} from '@markant-platform/shared/backoffice/model';
import { CartridgeDto } from '@markant-platform/shared/cartridge/model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MarkantSelectOption } from '@markant/elements-angular';
import { TranslocoService } from '@jsverse/transloco';
import { OrganizationDetailData, OrganizationType } from 'data-model';

export type CartridgeSelection = {
  cartridgeId: string;
  cartridgeName?: string;
  variantId: string;
  variantName?: string;
  platformBilling?: string;
  bookingReference?: string;
  overwriteCentAmount?: number;
};

type CartridgeFormGroup = {
  selected: FormControl<boolean | null>;
  cartridge: FormControl<{ cartridgeId: string; cartridgeName: string } | null>;
  cartridgeVariantId: FormControl<string | null>;
  platformBilling: FormControl<string | null>;
  overwriteCentAmount: FormControl<string | null>;
  bookingReference: FormControl<string | null>;
  orgAdminId?: FormControl<string | null>;
};

export enum steps {
  CartridgeSelection,
  OrganizationDataCompletion,
  Confirmation,
}

@Component({
  selector: 'markant-platform-organization-add-cartridges-dialog',
  templateUrl: './organization-add-cartridges-dialog.component.html',
  styleUrls: ['./organization-add-cartridges-dialog.component.scss'],
})
export class OrganizationAddCartridgesDialogComponent implements OnInit {
  @Input({ required: true }) isDialogOpen!: boolean;

  get organization(): OrganizationDetailData | undefined {
    return this._organization;
  }

  @Input()
  set organization(organization: OrganizationDetailData | undefined) {
    if (!organization) {
      this.isNewOrg = true;
    } else {
      this._organization = organization as Required<OrganizationDetailData>;
      this.organizationCompletionData = {
        masterKin: organization.masterKin ?? '',
        billingEmail: organization.billingEmail ?? '',
        billingLanguageIsoCode: organization.billingLanguageIsoCode ?? '',
      };
    }
  }

  @Input() preSelection?: CartridgeSelection[];
  @Input() singleSelectionId?: string | null;
  @Input() organizationType?: OrganizationType | null;
  @Input() organizationName?: string | null;
  @Input() organizationCompletionData: { masterKin: string; billingEmail: string; billingLanguageIsoCode: string } = {
    billingEmail: '',
    masterKin: '',
    billingLanguageIsoCode: '',
  };

  @Output() closeDialog = new EventEmitter<{ updatedOrganization?: OrganizationDetailData }>();
  @Output() organizationUpdated = new EventEmitter<OrganizationDetailData>();
  @Output() updateSelection = new EventEmitter<{
    cartridgeSelection: CartridgeSelection[];
    masterKin: string;
    billingEmail: string;
    billingLanguageIsoCode: string;
  }>();

  get cartridgesFormArray(): FormArray<FormGroup> {
    return (this.cartridgesForm as FormGroup)?.get('cartridges') as FormArray<FormGroup>;
  }

  activeLanguage$!: Observable<string>;
  cartridgesForm?: FormGroup<{ cartridges: FormArray }>;
  organizationCompletionForm?: FormGroup;
  organizationAdmins?: UserDTO[];
  variantData: Record<string, BookingCartridgeVariantData[]> = {};
  steps = steps;
  currentStep: steps = steps.CartridgeSelection;
  submitted: 'INVALID' | 'VALID' | false = false;
  submittedOrganizationCompletionForm: 'INVALID' | 'VALID' | false = false;
  isNewOrg = false;
  isLoading = false;

  private _organization?: Required<OrganizationDetailData>;

  readonly NOT_APPLICABLE: string = 'N/A';

  protected readonly validIsoCodes = [
    {
      shorthand: 'en-GB',
      displayName: 'EN',
    },
    {
      shorthand: 'de-DE',
      displayName: 'DE',
    },
  ];

  variantOptions: Record<string, MarkantSelectOption[]> = {};
  platformBillingOptions = [
    {
      value: 'true',
      representation: this.translocoService.translate('common.yes'),
    },
    {
      value: 'false',
      representation: this.translocoService.translate('common.no'),
    },
  ];
  orgAdminOptions: MarkantSelectOption[] = [];
  billingLanguageIsoCodeOptions = this.validIsoCodes.map(code => ({
    value: code.shorthand,
    representation: this.translocoService.translate('languageSelect.' + code.shorthand.toLowerCase()),
  })) as MarkantSelectOption[];

  constructor(
    private readonly backofficeCartridgeService: BackofficeCartridgeService,
    private readonly userService: UserService,
    private readonly organizationService: OrganizationService,
    private readonly snackbarService: SnackbarService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef,
    private readonly cmsLanguageService: CMSLanguageService,
    private readonly translocoService: TranslocoService
  ) {}

  async ngOnInit(): Promise<void> {
    this.activeLanguage$ = this.cmsLanguageService.activeCMSLanguage$;
    let cartridges: BookingCartridgeData[];

    if (!this.isNewOrg && this._organization) {
      cartridges = await firstValueFrom(
        this.backofficeCartridgeService.getUnbookedCartridgesForOrganization(this._organization.id).pipe(
          catchError((error: HttpErrorResponse) => {
            this.closeOnError('fetchCartridges', error.error['traceId']);
            return [];
          })
        )
      );

      this.organizationAdmins = await firstValueFrom(
        this.userService.getUsersForRole(this._organization.id, StaticRole.ORGANIZATION_ADMIN).pipe(
          tap(admins => {
            this.orgAdminOptions = (
              admins && admins.length > 0
                ? admins.map(admin => ({
                    value: admin.id,
                    representation: `${admin.firstname} ${admin.lastname} (${admin.email})`,
                  }))
                : [
                    {
                      value: '',
                      representation: this.translocoService.translate('usageRight.addCartridgesDialog.table.noAdmins'),
                    },
                  ]
            ) as MarkantSelectOption[];
          }),
          catchError((error: HttpErrorResponse) => {
            this.closeOnError('fetchOrgAdmins', error.error['traceId']);
            return [];
          })
        )
      );
    } else {
      const organizationType = this.organizationType ?? OrganizationType.SUPPLIER;
      const allCartridgeVariants: CartridgeDto[] = await firstValueFrom(
        this.backofficeCartridgeService.getAllCartridgeVariantsWithDemo(organizationType, true)
      );

      cartridges = this.mapCartridgeDTOToBookingCartridgeData(allCartridgeVariants);
    }

    cartridges = this.sortCartridges(cartridges);

    this.cartridgesForm = this.createCartridgesForm(cartridges);
    this.organizationCompletionForm = this.createOrganizationCompletionForm();

    this.variantData = cartridges.reduce(
      (cartridgeIdToVariantsMap: Record<string, BookingCartridgeVariantData[]>, cartridge) => {
        cartridgeIdToVariantsMap[cartridge.cartridgeId] = cartridge.bookingCartridgeVariants;
        return cartridgeIdToVariantsMap;
      },
      {}
    );

    this.variantOptions = Object.entries(this.variantData).reduce(
      (pre, entry) => ({
        ...pre,
        [entry[0]]: entry[1].map(data => ({
          value: data.cartridgeVariantId,
          representation: data.cartridgeVariantName,
        })),
      }),
      {}
    );

    // Don't remove. Required in the case user opens the dialog in existing customer registration.
    this.changeDetector.markForCheck();
  }

  toggleAllCartridges(checked: boolean, cartridgesFormArray: FormArray<FormGroup>): void {
    cartridgesFormArray.controls.forEach(control => {
      (control.get('selected') as FormControl).setValue(checked);
    });
  }

  allCartridgesSelected(cartridgesFormArray: FormArray<FormGroup>): boolean {
    return cartridgesFormArray.controls.every(control => (control.get('selected') as FormControl).value);
  }

  hasNoSelection(cartridgesFormArray: FormArray<FormGroup>): boolean {
    return cartridgesFormArray.controls.every(control => !(control.get('selected') as FormControl).value);
  }

  getVariantNameById(cartridgeId: string, cartridgeVariantId: string): string {
    return this.variantData[cartridgeId].filter(variant => variant.cartridgeVariantId === cartridgeVariantId)[0]
      .cartridgeVariantName;
  }

  getOrgAdminById(orgAdminId: string): UserDTO {
    return this.organizationAdmins?.filter(orgAdmin => orgAdmin.id === orgAdminId)[0] as UserDTO;
  }

  get selectedCartridges(): {
    cartridgeName: string;
    cartridgeId: string;
    cartridgeVariantId: string;
    platformBilling: string;
    overwriteCentAmount: string;
    bookingReference: string;
    orgAdminId: string;
  }[] {
    return this.cartridgesFormArray?.controls
      .filter(control => (control.get('selected') as FormControl).value)
      .map(control => {
        return {
          cartridgeName: (control.get('cartridge') as FormControl).value.cartridgeName,
          cartridgeId: (control.get('cartridge') as FormControl).value.cartridgeId,
          cartridgeVariantId: (control.get('cartridgeVariantId') as FormControl).value,
          platformBilling: (control.get('platformBilling') as FormControl).value,
          overwriteCentAmount: (control.get('overwriteCentAmount') as FormControl).value,
          bookingReference: (control.get('bookingReference') as FormControl).value,
          orgAdminId: !this.isNewOrg ? (control.get('orgAdminId') as FormControl).value : undefined,
        };
      });
  }

  async orderSelectedCartridges(organizationData: OrganizationDetailData | undefined): Promise<void> {
    this.isLoading = true;
    if (!organizationData || !this.organizationCompletionForm) {
      this.isLoading = false;
      return;
    }

    const updatedOrganization: OrganizationDetailData = await firstValueFrom(
      this.organizationService
        .setOrganizationDetailsById(organizationData.id as string, organizationData, {
          ...organizationData,
          ...this.organizationCompletionForm.getRawValue(),
        })
        .pipe(
          catchError((error: HttpErrorResponse) => {
            this.handleOrganizationUpdateError(error);
            this.isLoading = false;
            return EMPTY;
          })
        )
    );

    const draft: BookingEzrDraft = {
      cartridgeBookingRequests: this.selectedCartridges.map(cartridge => {
        const orgAdmin = this.getOrgAdminById(cartridge.orgAdminId);

        return {
          cartridgeId: cartridge.cartridgeId,
          cartridgeVariantId: cartridge.cartridgeVariantId,
          userId: cartridge.orgAdminId,
          platformBilling: cartridge.platformBilling === 'true',
          overwriteCentAmount: this.getFinalOverwriteCentAmount(cartridge.overwriteCentAmount),
          bookingReference: cartridge.bookingReference,
          userDetails: {
            ...orgAdmin,
            organization: {
              ...(updatedOrganization as Required<OrganizationDetailData>),
              customerNumber: '' + updatedOrganization.customerNumber,
              organizationId: updatedOrganization.id as string,
            },
          },
        };
      }),
      organizationId: updatedOrganization.id as string,
    };

    this.organizationUpdated.emit(updatedOrganization);

    await firstValueFrom(
      this.backofficeCartridgeService.orderSelectedCartridges(draft).pipe(
        catchError((error: HttpErrorResponse) => {
          this.closeOnError('orderCartridges', error.error['traceId']);
          this.isLoading = false;
          return EMPTY;
        })
      )
    );

    this.snackbarService.showSuccessSnackbarWithTransloco('usageRight.addCartridgesDialog.success');
    this.closeDialog.emit();
    this.isLoading = false;
  }

  /**
   * Force centAmount to be a string because currency input always emit a number and in some cases we have 'N/A' value.
   *
   * @param newCentAmount centAmount input value (Type: number)
   * @param formControlIndex Form Control index.
   */
  onOverwriteCentAmountChanged(newCentAmount: number, formControlIndex: number): void {
    const { controls } = this.cartridgesForm?.controls.cartridges.controls[formControlIndex] as FormGroup;
    controls['overwriteCentAmount'].setValue(`${newCentAmount}`);
  }

  updateCartridgeSelection(): void {
    this.isLoading = true;
    const data = {
      cartridgeSelection: this.selectedCartridges.map(
        ({ bookingReference, cartridgeId, cartridgeVariantId: variantId, platformBilling, overwriteCentAmount }) => ({
          cartridgeId,
          variantId,
          platformBilling,
          bookingReference,
          overwriteCentAmount: this.getFinalOverwriteCentAmount(overwriteCentAmount),
        })
      ),
      ...this.organizationCompletionForm?.getRawValue(),
    };

    this.updateSelection.emit(data);
    this.closeDialog.emit();
    this.isLoading = false;
  }

  /**
   * TrackBy function for the ngFor directive to prevent child popover to render entries multiple times
   */
  trackByFn(index: number): number {
    return index;
  }

  onPlatformBillingChanged(rowIndex: number): void {
    const indexFormGroup = this.cartridgesForm?.controls.cartridges.controls[rowIndex] as FormGroup;
    const platformBillingFormControl = indexFormGroup.controls['platformBilling'] as FormControl;
    const overwriteCentAmountFormControl = indexFormGroup.controls['overwriteCentAmount'] as FormControl;

    const { value } = platformBillingFormControl;

    if (value) {
      if (value === 'true') {
        overwriteCentAmountFormControl.addValidators([Validators.required, Validators.min(1)]);
      } else {
        overwriteCentAmountFormControl.removeValidators(Validators.required);
        overwriteCentAmountFormControl.setValidators([Validators.min(0), Validators.max(999999999999)]);
      }
      overwriteCentAmountFormControl.updateValueAndValidity();
    }
  }

  private createCartridgesForm(cartridges: BookingCartridgeData[]): FormGroup<{ cartridges: FormArray }> {
    const cartridgesFormGroup: FormGroup[] = cartridges.map(cartridge => {
      const selection = this.preSelection?.find(({ cartridgeId }) => cartridgeId === cartridge.cartridgeId);
      const selectedVariant = selection?.variantId as string;
      const platformBilling = selection?.platformBilling as string;
      const bookingReference = selection?.bookingReference as string;
      const overwriteCentAmount = selection
        ? this.getOverwriteCentAmountFromSelection(
            cartridge.bookingCartridgeVariants,
            selectedVariant,
            selection.overwriteCentAmount
          )
        : '';

      const formGroup: FormGroup<CartridgeFormGroup> = new FormGroup<CartridgeFormGroup>({
        selected: new FormControl<boolean>(!!selectedVariant),
        cartridge: new FormControl<{ cartridgeId: string; cartridgeName: string }>({
          value: {
            cartridgeId: cartridge.cartridgeId,
            cartridgeName: cartridge.cartridgeName,
          },
          disabled: true,
        }),
        cartridgeVariantId: new FormControl<string | null>(
          {
            value: selectedVariant || null,
            disabled: !selectedVariant,
          },
          Validators.required
        ),
        platformBilling: new FormControl<string | null>(
          {
            value: platformBilling,
            disabled: !selectedVariant,
          },
          Validators.required
        ),
        overwriteCentAmount: new FormControl<string | null>(
          {
            value: overwriteCentAmount,
            disabled: !selectedVariant || overwriteCentAmount === this.NOT_APPLICABLE,
          },
          [Validators.max(999999999999)]
        ),
        bookingReference: new FormControl<string | null>({ value: bookingReference, disabled: !selectedVariant }),
      });

      if (!this.isNewOrg) {
        formGroup.addControl(
          'orgAdminId',
          new FormControl<string | null>(
            {
              value: null,
              disabled: true,
            },
            Validators.required
          )
        );
      }

      this.addListener(cartridges, formGroup);
      this.checkDisablePlatformBilling(cartridges, formGroup);

      return formGroup;
    });

    return new FormGroup({
      cartridges: new FormArray(cartridgesFormGroup),
    });
  }

  private createOrganizationCompletionForm(): FormGroup {
    const organizationCompletionForm = new FormGroup({
      masterKin: new FormControl(this.organizationCompletionData.masterKin, [
        Validators.required,
        Validators.pattern(RegexPattern.MASTER_KIN),
      ]),
      billingEmail: new FormControl(this.organizationCompletionData.billingEmail, [
        Validators.required,
        Validators.pattern(RegexPattern.EMAIL_PATTERN),
      ]),
      billingLanguageIsoCode: new FormControl(
        this.organizationCompletionData.billingLanguageIsoCode,
        Validators.required
      ),
    });

    if (this.organizationCompletionData?.masterKin !== '') {
      (organizationCompletionForm.get('masterKin') as FormControl).disable();
    }
    if (this.organizationCompletionData?.billingEmail !== '') {
      (organizationCompletionForm.get('billingEmail') as FormControl).disable();
    }

    if (this.organizationCompletionData?.billingLanguageIsoCode !== '') {
      (organizationCompletionForm.get('billingLanguageIsoCode') as FormControl).disable();
    }

    return organizationCompletionForm;
  }

  private addListener(cartridges: BookingCartridgeData[], cartridgeFormGroup: FormGroup): void {
    const selectedFormControl = cartridgeFormGroup.get('selected') as FormControl<boolean | null>;

    selectedFormControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(selected => {
      if (selected) {
        (cartridgeFormGroup.get('cartridgeVariantId') as FormControl).enable();
        (cartridgeFormGroup.get('bookingReference') as FormControl).enable();
        if (!this.isNewOrg) {
          (cartridgeFormGroup.get('orgAdminId') as FormControl).enable();
        }
      } else {
        (cartridgeFormGroup.get('cartridgeVariantId') as FormControl).disable();
        (cartridgeFormGroup.get('bookingReference') as FormControl).disable();
        (cartridgeFormGroup.get('platformBilling') as FormControl).disable();
        (cartridgeFormGroup.get('overwriteCentAmount') as FormControl).disable();

        (cartridgeFormGroup.get('cartridgeVariantId') as FormControl).setValue(null);
        (cartridgeFormGroup.get('bookingReference') as FormControl).setValue(null);
        (cartridgeFormGroup.get('platformBilling') as FormControl).setValue(null);
        (cartridgeFormGroup.get('overwriteCentAmount') as FormControl).setValue(null);

        if (!this.isNewOrg) {
          (cartridgeFormGroup.get('orgAdminId') as FormControl).disable();
          (cartridgeFormGroup.get('orgAdminId') as FormControl).setValue(null);
        }
      }
    });

    const cartridgeVariantFormControl = cartridgeFormGroup.get('cartridgeVariantId') as FormControl<string | null>;

    cartridgeVariantFormControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(cartridgeVariantId => {
      const platformBillingControl = cartridgeFormGroup.get('platformBilling') as FormControl;

      if (cartridgeVariantFormControl.value) {
        const cartridgeFormControl = cartridgeFormGroup.get('cartridge') as FormControl<{
          cartridgeId: string;
          cartridgeName: string;
        }>;
        const cartridge = cartridges.find(({ cartridgeId }) => cartridgeId === cartridgeFormControl.value.cartridgeId);

        const bookingCartridgeVariant = cartridge?.bookingCartridgeVariants.find(
          bookingCartridgeVar => bookingCartridgeVar.cartridgeVariantId === cartridgeVariantId
        );

        this.updateOverwriteCentAmountStatus(
          cartridgeFormGroup.get('overwriteCentAmount') as FormControl,
          cartridge?.cartridgeId ?? '',
          cartridgeVariantId ?? ''
        );

        if (bookingCartridgeVariant) {
          if (bookingCartridgeVariant.isPlatformBillingActive) {
            platformBillingControl.enable();
            platformBillingControl.setValue(null);
          } else {
            platformBillingControl.setValue('false');
            platformBillingControl.disable();
          }
        }
      }
    });
  }

  private closeOnError(source: 'fetchCartridges' | 'fetchOrgAdmins' | 'orderCartridges', traceId: string): void {
    this.snackbarService.showErrorSnackbarWithTransloco('usageRight.addCartridgesDialog.error.' + source, { traceId });
    this.closeDialog.emit();
  }

  private mapCartridgeDTOToBookingCartridgeData(cartridges: CartridgeDto[]): BookingCartridgeData[] {
    const cartridgeData: BookingCartridgeData[] = [];
    cartridges
      .filter(cartridge => cartridge.status === 'ACTIVE')
      .forEach(cartridge => {
        const data = cartridgeData.find(({ cartridgeId }) => cartridgeId === cartridge.cartridgeId);

        if (!data) {
          cartridgeData.push({
            cartridgeId: cartridge.cartridgeId,
            cartridgeName: cartridge.name,
            bookingCartridgeVariants: [
              {
                cartridgeVariantId: cartridge.variantId,
                cartridgeVariantName: cartridge.variantName,
                isPlatformBillingActive: this.checkIfPlatformBillingActive(cartridge),
                price: cartridge.price,
              },
            ],
          });
        } else {
          data.bookingCartridgeVariants.push({
            cartridgeVariantId: cartridge.variantId,
            cartridgeVariantName: cartridge.variantName,
            isPlatformBillingActive: this.checkIfPlatformBillingActive(cartridge),
            price: cartridge.price,
          });
        }
      });

    return cartridgeData;
  }

  getCartridgeVariantName(cartridgeId: string, variantId: string | string[] | undefined): string {
    const cartridge: BookingCartridgeVariantData[] = this.variantData[cartridgeId];
    return cartridge.find(variant => variant.cartridgeVariantId === variantId)?.cartridgeVariantName ?? '';
  }

  getOrgAdminNameEmail(userId: string | string[] | undefined): string {
    const admin = this.organizationAdmins?.find(({ id }) => id === userId);

    if (!admin) {
      return '';
    }

    return (
      (admin.firstname ? `${admin.firstname} ` : '') +
      (admin.lastname ? `${admin.lastname} ` : '') +
      (admin.email ? ` ( ${admin.email})` : '')
    );
  }

  setFormSubmitted(): void {
    if (this.cartridgesForm && this.cartridgesForm.valid) {
      this.submitted = 'VALID';

      const isAtLeastOnePlatformBilling = this.checkIfAtLeastOnePlatformBillingSetTrue();

      if (this.organizationCompletionData?.billingLanguageIsoCode === '' && isAtLeastOnePlatformBilling) {
        (this.organizationCompletionForm?.get('billingLanguageIsoCode') as FormControl).setValue(
          this.validIsoCodes[1].shorthand
        );
      }

      this.currentStep =
        isAtLeastOnePlatformBilling && !this.singleSelectionId ? steps.OrganizationDataCompletion : steps.Confirmation;
    } else {
      this.submitted = 'INVALID';
    }
  }

  reenableForm(): void {
    this.submitted = false;
    this.currentStep = steps.CartridgeSelection;
    this.cartridgesFormArray.controls.forEach(control => {
      if (!(control.get('selected') as FormControl).value) {
        control.get('cartridgeVariantId')?.disable();
        control.get('orgAdminId')?.disable();
        control.get('platformBilling')?.disable();
        control.get('overwriteCentAmount')?.disable();
        control.get('bookingReference')?.disable();
      }
    });
  }

  nextToConfirmation(): void {
    if (this.organizationCompletionForm?.valid || this.organizationCompletionForm?.disabled) {
      this.currentStep = steps.Confirmation;
      this.submittedOrganizationCompletionForm = 'VALID';
    } else {
      this.submittedOrganizationCompletionForm = 'INVALID';
    }
  }

  backToOrganizationDataCompletion(): void {
    if (this.checkIfAtLeastOnePlatformBillingSetTrue() && !this.singleSelectionId) {
      this.currentStep = steps.OrganizationDataCompletion;
    } else {
      this.reenableForm();
    }

    this.submittedOrganizationCompletionForm = false;
  }

  checkIfAtLeastOnePlatformBillingSetTrue(): boolean {
    return this.cartridgesFormArray.controls.some(control => {
      const selected = control.get('selected')?.value;
      const platformBilling = control.get('platformBilling')?.value === 'true';

      return selected && platformBilling;
    });
  }

  checkIfPlatformBillingActive(cartridge: CartridgeDto): boolean {
    return cartridge.prices?.some(
      price =>
        price.targetAudience?.ezr &&
        this.organizationType &&
        price.targetAudience.organizationType === this.organizationType
    );
  }

  checkDisablePlatformBilling(cartridges: BookingCartridgeData[], cartridgeFormGroup: FormGroup): void {
    const selectedFormControl = cartridgeFormGroup.get('selected') as FormControl<boolean | null>;
    const cartridgeFormControl = cartridgeFormGroup.get('cartridge') as FormControl<{
      cartridgeId: string;
      cartridgeName: string;
    } | null>;
    const cartridgeVariantFormControl = cartridgeFormGroup.get('cartridgeVariantId') as FormControl<string | null>;
    const platformBillingControl = cartridgeFormGroup.get('platformBilling') as FormControl;

    if (cartridgeVariantFormControl.value && selectedFormControl.value) {
      const singleCartridge = cartridges.find(
        ({ cartridgeId }) => cartridgeId === cartridgeFormControl.value?.cartridgeId
      );
      const bookingCartridgeVariant = singleCartridge?.bookingCartridgeVariants.find(
        ({ cartridgeVariantId }) => cartridgeVariantId === cartridgeVariantFormControl.value
      );

      if (!bookingCartridgeVariant) {
        return;
      }

      if (bookingCartridgeVariant.isPlatformBillingActive) {
        platformBillingControl.enable();
      } else {
        platformBillingControl.setValue('false');
        platformBillingControl.disable();
      }
    }
  }

  private handleOrganizationUpdateError(error: HttpErrorResponse): void {
    const traceId: string = error.error['traceId'];

    if (error && error.status === 400 && error.error?.message?.includes('Error-Code: 3002')) {
      this.snackbarService.showErrorSnackbarWithTransloco('usageRight.addCartridgesDialog.error.masterKinDuplicate', {
        traceId,
      });
    } else {
      this.snackbarService.showErrorSnackbarWithTransloco('usageRight.addCartridgesDialog.error.organizationUpdate', {
        traceId,
      });
    }
  }

  private updateOverwriteCentAmountStatus(
    overwriteCentAmountFormControl: FormControl,
    cartridgeIdValue: string,
    cartridgeVariantIdValue: string
  ): void {
    if (cartridgeIdValue && cartridgeVariantIdValue && overwriteCentAmountFormControl) {
      const variantSelected = this.variantData[cartridgeIdValue].find(
        ({ cartridgeVariantId }) => cartridgeVariantId === cartridgeVariantIdValue
      );

      if (variantSelected?.price) {
        const { price } = variantSelected;

        if (price.hasToBeRequested) {
          overwriteCentAmountFormControl.enable();
          overwriteCentAmountFormControl.setValue('');
        } else {
          this.disabledOverwriteCentAmountStatus(overwriteCentAmountFormControl);
        }
      } else {
        this.disabledOverwriteCentAmountStatus(overwriteCentAmountFormControl);
      }
    }
  }

  private disabledOverwriteCentAmountStatus(overwriteCentAmountFormControl: FormControl): void {
    overwriteCentAmountFormControl.disable();
    overwriteCentAmountFormControl.setValue(this.NOT_APPLICABLE);
  }

  private sortCartridges(cartridges: BookingCartridgeData[]): BookingCartridgeData[] {
    return cartridges.sort((a, b) => {
      if (a.cartridgeName && b.cartridgeName) {
        return a.cartridgeName.localeCompare(b.cartridgeName);
      }
      if (!a.cartridgeName && b.cartridgeName) {
        return 1;
      }
      if (a.cartridgeName && !b.cartridgeName) {
        return -1;
      }

      return 0;
    });
  }

  private convertOverwriteCentAmountToNumber(centAmount: string): number | null {
    return centAmount ? parseFloat(centAmount) * 100 : null;
  }

  private getFinalOverwriteCentAmount(centAmount: string): number | null {
    return centAmount === this.NOT_APPLICABLE ? null : this.convertOverwriteCentAmountToNumber(centAmount);
  }

  private getOverwriteCentAmountFromSelection(
    bookingCartridgeVariants: BookingCartridgeVariantData[],
    selectedCartridgeVariant: string,
    overwriteCentAmount?: number | null
  ): string {
    const variant = bookingCartridgeVariants.find(
      ({ cartridgeVariantId }) => selectedCartridgeVariant === cartridgeVariantId
    );

    if (overwriteCentAmount === null && variant && !variant.price?.hasToBeRequested) {
      return this.NOT_APPLICABLE;
    }

    return overwriteCentAmount ? `${overwriteCentAmount / 100}` : '';
  }

  onVariantSelect(rowIndex: number): void {
    const indexFormGroup = this.cartridgesForm?.controls.cartridges.controls[rowIndex] as FormGroup;
    const cartridgeFormControl = indexFormGroup.controls['cartridge'] as FormControl;
    const variantIdFormControl = indexFormGroup.controls['cartridgeVariantId'] as FormControl;
    const billingViaPlatformFormControl = indexFormGroup.controls['platformBilling'] as FormControl;

    const cartridgeId = cartridgeFormControl.value.cartridgeId;
    const variantId = variantIdFormControl.value;

    if (cartridgeId && variantId) {
      const variantSelected = this.variantData[cartridgeId].find(
        ({ cartridgeVariantId }) => cartridgeVariantId === variantId
      );

      if (variantSelected?.price) {
        const { price } = variantSelected;

        if (price.targetAudience.ezr && !price.hasToBeRequested && price.centAmount == 0) {
          billingViaPlatformFormControl.disable();
          billingViaPlatformFormControl.setValue('false');
        } else {
          billingViaPlatformFormControl.enable();
          billingViaPlatformFormControl.setValue(null);
        }
      }
    }
  }
}
